import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from "lucide-react";

import { cn } from "@/lib/utils";

const alertVariants = cva(
    "setu:relative setu:w-full setu:rounded-lg setu:!border setu:p-4 [&>svg~*]:setu:pl-7 [&>svg+div]:setu:translate-y-[-3px] [&>svg]:setu:absolute [&>svg]:setu:left-4 [&>svg]:setu:top-4 [&>svg]:setu:text-foreground",
    {
        variants: {
            variant: {
                default: "setu:bg-background setu:text-foreground",
                destructive:
                    "setu:!border-destructive/50 setu:text-destructive dark:setu:!border-destructive [&>svg]:setu:text-destructive",
                success:
                    "setu:!border-green-500/50 setu:text-green-700 setu:bg-green-50 dark:setu:text-green-400 dark:setu:bg-green-950/50 [&>svg]:setu:text-green-600 dark:[&>svg]:setu:text-green-400",
                warning:
                    "setu:!border-orange-500/50 setu:text-orange-700 setu:bg-orange-50 dark:setu:!border-orange-500 dark:setu:text-orange-400 dark:setu:bg-orange-950/50 [&>svg]:setu:text-orange-600 dark:[&>svg]:setu:text-orange-400",
                info: "setu:!border-blue-500/50 setu:text-blue-700 setu:bg-blue-50 dark:setu:!border-blue-500 dark:setu:text-blue-400 dark:setu:bg-blue-950/50 [&>svg]:setu:text-blue-600 dark:[&>svg]:setu:text-blue-400",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ className, variant, ...props }, ref) => {
    return <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />;
});
Alert.displayName = "Alert";

interface AlertTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

const AlertTitle = React.forwardRef<HTMLParagraphElement, AlertTitleProps>(({ className, ...props }, ref) => {
    return (
        <h5
            ref={ref}
            className={cn("setu:mb-1 setu:font-medium setu:leading-none setu:tracking-tight", className)}
            {...props}
        />
    );
});
AlertTitle.displayName = "AlertTitle";

interface AlertDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const AlertDescription = React.forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
    ({ className, ...props }, ref) => {
        return <div ref={ref} className={cn("setu:text-sm [&_p]:setu:leading-relaxed", className)} {...props} />;
    }
);
AlertDescription.displayName = "AlertDescription";

// Fictoan compatibility interfaces and components
export interface FictoanCalloutProps {
    kind: "info" | "success" | "warning" | "error";
    children: React.ReactNode;
    className?: string;
    // Fictoan common props
    margin?: "none" | "nano" | "micro" | "tiny" | "small" | "medium" | "large" | "huge";
    padding?: "none" | "nano" | "micro" | "tiny" | "small" | "medium" | "large" | "huge";
    bgColour?: string;
    textColour?: string;
    borderColour?: string;
    shadow?: "none" | "mild" | "soft" | "hard";
    isFullWidth?: boolean;
}

export interface FictoanNotificationItemProps extends FictoanCalloutProps {
    show: boolean;
    isDismissible?: boolean;
    onCloseCallback: () => void;
    showFor?: number;
}

// Helper function to get variant icons
const getVariantIcon = (variant: "default" | "destructive" | "success" | "warning" | "info") => {
    switch (variant) {
        case "success":
            return <CheckCircle className="h-4 w-4" />;
        case "warning":
            return <AlertTriangle className="h-4 w-4" />;
        case "info":
            return <Info className="h-4 w-4" />;
        case "destructive":
            return <AlertCircle className="h-4 w-4" />;
        default:
            return <Info className="h-4 w-4" />;
    }
};

// Helper function to map Fictoan kind to shadcn variant
const mapFictoanKindToVariant = (kind: "info" | "success" | "warning" | "error") => {
    switch (kind) {
        case "error":
            return "destructive" as const;
        case "success":
            return "success" as const;
        case "warning":
            return "warning" as const;
        case "info":
            return "info" as const;
        default:
            return "default" as const;
    }
};

// Fictoan-compatible Callout wrapper (static alerts)
interface CalloutProps extends FictoanCalloutProps {}

const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(({ kind, children, className }, ref) => {
    const variant = mapFictoanKindToVariant(kind);
    const icon = getVariantIcon(variant);

    return (
        <Alert ref={ref} variant={variant} className={className}>
            {icon}
            <AlertDescription>{children}</AlertDescription>
        </Alert>
    );
});
Callout.displayName = "Callout";

// Fictoan-compatible NotificationItem wrapper (dismissible alerts)
interface NotificationItemProps extends FictoanNotificationItemProps {}

const NotificationItem = React.forwardRef<HTMLDivElement, NotificationItemProps>(
    ({ kind, children, show, isDismissible = true, onCloseCallback, showFor = 8000, className }, ref) => {
        const [isVisible, setIsVisible] = React.useState(show);
        const variant = mapFictoanKindToVariant(kind);
        const icon = getVariantIcon(variant);

        // Auto-dismiss functionality
        React.useEffect(() => {
            if (show && showFor > 0) {
                const timer = setTimeout(() => {
                    setIsVisible(false);
                    onCloseCallback();
                }, showFor);

                return () => clearTimeout(timer);
            }
        }, [show, showFor, onCloseCallback]);

        // Update visibility when show prop changes
        React.useEffect(() => {
            setIsVisible(show);
        }, [show]);

        if (!isVisible) {
            return null;
        }

        const handleDismiss = () => {
            setIsVisible(false);
            onCloseCallback();
        };

        return (
            <Alert ref={ref} variant={variant} className={cn("setu:relative", className)}>
                {icon}
                <AlertDescription className="setu:pr-8">{children}</AlertDescription>
                {isDismissible && (
                    <button
                        onClick={handleDismiss}
                        className="setu:absolute setu:right-2 setu:top-2 setu:rounded-md setu:p-1 setu:text-current setu:opacity-70 hover:setu:opacity-100 focus:setu:opacity-100 focus:setu:outline-hidden focus:setu:ring-2 focus:setu:ring-ring focus:setu:ring-offset-2"
                        aria-label="Close notification"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </Alert>
        );
    }
);
NotificationItem.displayName = "NotificationItem";

// Simple NotificationsWrapper for positioning (static implementation)
export interface FictoanNotificationsWrapperProps {
    children: React.ReactNode;
    position?: "left" | "right";
    anchor?: "top" | "bottom";
    order?: "new-on-top" | "new-on-bottom";
    className?: string;
}

const NotificationsWrapper = React.forwardRef<HTMLDivElement, FictoanNotificationsWrapperProps>(
    ({ children, position = "right", anchor = "top", order = "new-on-top", className }, ref) => {
        const positionClasses = cn(
            "setu:fixed setu:z-50 setu:flex setu:flex-col setu:gap-2 setu:w-full setu:max-w-sm",
            {
                "setu:left-4": position === "left",
                "setu:right-4": position === "right",
                "setu:top-4": anchor === "top",
                "setu:bottom-4": anchor === "bottom",
                "setu:flex-col": order === "new-on-top",
                "setu:flex-col-reverse": order === "new-on-bottom",
            },
            className
        );

        return (
            <div ref={ref} className={positionClasses}>
                {children}
            </div>
        );
    }
);
NotificationsWrapper.displayName = "NotificationsWrapper";

// Export components
export { Alert, AlertTitle, AlertDescription, Callout, NotificationItem, NotificationsWrapper };
